import * as React from 'react';
import { xor } from 'lodash';

import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionLink from 'components/buttons/action-link/ActionLink';
import InputRadio from 'components/inputs/input-radio/InputRadio';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import { FormField } from 'components/form';
import Tooltip from 'components/Tooltip';
import { withModal } from 'components/modal/ModalHOC';

import BulkAddToShortlistModal from 'modules/shortlist/components/BulkAddToShortlistModal';

import { extendExternalPageToken } from 'modules/lists/reducers/lists';

import { __t, applyTranslations } from 'utils/language';

import type { SyntheticEvent } from 'react';
import type { ProposalListInfo, TextEntry } from 'types';
import type { ProposalTileChannelType, ProposalTileChannelTypeOptions } from 'modules/proposal/types/ProposalTile.type';

import './proposal-page-header.scss';

applyTranslations(require('./proposal-page-header.i18n.json'));

const ActionButtonWithBulkAddToShortlistModal = withModal(ActionButton, {
  modalContent: BulkAddToShortlistModal
});

type HeadlineOption = 'with' | 'without';

const TILE_TEMPLATE = ['basic', 'advanced'];
const TILE_CHANNELS: Array<ProposalTileChannelTypeOptions> = ['instagram', 'tiktok', 'custom'];
const TILE_HEADLINES: Array<HeadlineOption> = ['with', 'without'];

type Props = {
  listInfo: ProposalListInfo | undefined,
  organizationId: number,
  clientApprovalLink?: string,
  currentTemplate: string,
  canUpdateChannels: boolean,
  currentChannels: Array<ProposalTileChannelTypeOptions>,
  hasYoutubeOnly?: boolean,
  hasApprovedCreators?: boolean,
  shouldShowHeadlines: boolean,
  isReportDownloading: boolean,
  onReportDownload: () => void,
  onTemplateChange: (selectedTemplate: string) => void,
  onHeadlineChange: (isShowingHeadline: boolean) => void,
  onChannelChange: (selectedChannels: Array<ProposalTileChannelTypeOptions>) => void
};

const channelsRequiredForCustomMode: Array<ProposalTileChannelType> = ['instagram', 'tiktok'];

class ProposalPageHeader extends React.Component<Props> {
  static defaultProps = {
    currentTemplate: TILE_TEMPLATE[0]
  };

  isExtendingRequestPending = false;

  handleTemplateChange = (event: SyntheticEvent<TextEntry>) => {
    this.props.onTemplateChange(event.currentTarget.value);
  };

  handleHeadlinesChange = (event: SyntheticEvent<TextEntry>) => {
    this.props.onHeadlineChange(event.currentTarget.value === 'with');
  };

  handleChannelChange = (event: SyntheticEvent<TextEntry>) => {
    const { onChannelChange, currentChannels } = this.props;

    let newChannels = xor(currentChannels, [event.currentTarget.value as ProposalTileChannelTypeOptions]);

    if (newChannels.length === 0) {
      // do not allow deselecting all items
      return;
    }

    if (
      newChannels.includes('custom')
      && !channelsRequiredForCustomMode.every(channel => newChannels.includes(channel))
    ) {
      // drop selection from custom since not all channels are picked
      newChannels = newChannels.filter(channel => channel !== 'custom');
    }

    onChannelChange(newChannels);
  };

  render() {
    const {
      currentChannels,
      canUpdateChannels,
      currentTemplate,
      clientApprovalLink,
      hasYoutubeOnly = false,
      hasApprovedCreators = false,
      listInfo,
      organizationId,
      shouldShowHeadlines,
      isReportDownloading,
      onReportDownload
    } = this.props;

    const mappedListInfo = {
      id: listInfo?.creatorListId,
      label: listInfo?.creatorListName
    };

    const canUseCustomChannels = channelsRequiredForCustomMode
      .every(channel => currentChannels.includes(channel));

    return (
      <section className="proposal-page-header">
        <div className="proposal-page-header__top-container">
          <div className="proposal-page-header__info-section">
            <div className="proposal-page-header__title-container">
              <span className="proposal-page-header__title">
                {__t('proposal_page_header.title')}
              </span>

              <div className="proposal-page-header__clients-page-link">
                <Tooltip
                  showQuestionMark
                  width="extra-long"
                  textAlign="left"
                  message={__t('proposal_page_header.client_page_link_tooltip')}
                >
                  <ActionLink
                    className="proposal-page-header__clients-page-link-text"
                    color="secondary"
                    onClick={async (): Promise<false> => {
                      if (this.isExtendingRequestPending) {
                        return false;
                      }

                      if (listInfo?.creatorListId) {
                        this.isExtendingRequestPending = true;
                        await extendExternalPageToken(organizationId, listInfo?.creatorListId);
                        this.isExtendingRequestPending = false;
                      }

                      window.open(clientApprovalLink, '_blank');
                      return false;
                    }}
                  >
                    {__t('proposal_page_header.open_clients_page')}
                  </ActionLink>
                </Tooltip>
              </div>
            </div>

            <VerticalSpacing type="small" />

            <div className="proposal-page-header__info-text">
              <div>{__t('proposal_page_header.info_text')}</div>
              <div>{hasYoutubeOnly && __t('proposal_page_header.youtube_info_text')}</div>
            </div>
          </div>
          <div className="proposal-page-header__download-ppt">
            <ActionButton
              disabledWithSpinner
              onClick={onReportDownload}
              disabled={isReportDownloading}
            >
              {__t('proposal_page_header.download_ppt_label')}
            </ActionButton>
          </div>
          <div className="proposal-page-header__add-shortlisted">
            <ActionButtonWithBulkAddToShortlistModal
              disabled={!hasApprovedCreators}
              modalContentProps={{
                list: mappedListInfo,
                organizationId,
                showOnlyApproved: true
              }}
            >
              {__t('proposal_page_header.add_to_shortlist_label')}
            </ActionButtonWithBulkAddToShortlistModal>
          </div>
        </div>

        <VerticalSpacing type="medium" />

        <div className="proposal-page-header__footer">
          <FormField
            label={__t('proposal_page_header.template_type_label')}
            labelType="highlighted"
            direction="column"
            fontSize="small"
          >
            {
              TILE_TEMPLATE.map((option) => (
                <InputRadio
                  key={`tile-template--${option}`}
                  name="tile-template"
                  value={option}
                  id={`tile-template-option--${option}`}
                  checked={currentTemplate === option}
                  label={__t(`proposal_page_header.template_type_option.${option}`)}
                  onChange={this.handleTemplateChange}
                />
              ))
            }
          </FormField>
          <div className="proposal-page-header__channels">
            <Tooltip
              width="long"
              message={(!canUpdateChannels) ? __t('proposal_page_header.blocked_channels_tooltip') : undefined}
            >
              <FormField
                label={__t('proposal_page_header.channel_type_label')}
                labelType="highlighted"
                direction="column"
                fontSize="small"
              >
                {
                  TILE_CHANNELS.map((option) => {
                    const isDisabled = !canUpdateChannels
                      || (option === 'custom' && !canUseCustomChannels);

                    return (
                      <InputCheckbox
                        key={`tile-channel--${option}`}
                        name="tile-channel"
                        value={option}
                        size="small"
                        id={`tile-channel-option--${option}`}
                        disabled={isDisabled}
                        checked={currentChannels.includes(option)}
                        label={__t(`proposal_page_header.channel_type_option.${option}`)}
                        onChange={this.handleChannelChange}
                      />
                    );
                  })
                }
              </FormField>
            </Tooltip>
          </div>
          <FormField
            label={__t('proposal_page_header.headlines_settings_label')}
            labelType="highlighted"
            direction="column"
            fontSize="small"
          >
            {
              TILE_HEADLINES.map((option) => {
                const selectedOption: HeadlineOption = shouldShowHeadlines ? 'with' : 'without';
                return (
                  <InputRadio
                    key={`tile-headlines--${option}`}
                    name="tile-headlines"
                    value={option}
                    id={`tile-headlines-option--${option}`}
                    checked={option === selectedOption}
                    label={__t(`proposal_page_header.headlines_settings_option.${option}`)}
                    onChange={this.handleHeadlinesChange}
                  />
                );
              })
            }
          </FormField>
        </div>
      </section>
    );
  }
}

export default ProposalPageHeader;
